import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaTimes, FaDownload } from "react-icons/fa";
import { AppButton, AppSpinner } from "../app";
import { appColors, appRadius, appShadows, appType } from "../../utils/appTheme";
import { asambleaPropietario, urlDescargable } from "../../utils/asambleas/api";

/**
 * Visor del acta (PDF) y de la grabación de una asamblea, dentro de la app.
 * Todo se ve en la nube: el servidor da una URL firmada de pocos minutos
 * (acta) o de unas horas (grabación, para poder verla entera) y nunca un link
 * permanente. "Descargar" pide otra URL, con descarga como adjunto.
 */

const FOCUSABLES = 'button:not([disabled]), [href], iframe, video, audio, [tabindex]:not([tabindex="-1"])';

/**
 * Abre en una pestaña nueva lo que devuelva `pedir` (una respuesta {url, headers}).
 * La pestaña se abre en el mismo clic (antes del await) para que el navegador
 * no la trate como popup. → la URL si el navegador bloqueó la pestaña (para
 * mostrar un link), o "" si se abrió.
 */
export async function abrirEnPestana(pedir) {
  let ventana = null;
  try {
    ventana = window.open("", "_blank");
  } catch (_) {
    ventana = null;
  }
  try {
    const url = await urlDescargable(await pedir());
    if (ventana && !ventana.closed) {
      ventana.opener = null;
      ventana.location.replace(url);
      return "";
    }
    return url;
  } catch (e) {
    if (ventana && !ventana.closed) ventana.close();
    throw e;
  }
}

/** Marco del visor: diálogo modal con título, "Descargar", cerrar con Escape y el foco adentro. */
function Marco({ titulo, subtitulo, onCerrar, onDescargar, descargando, ancho, children }) {
  const { t } = useTranslation("asambleas");
  const idTitulo = useId();
  const panelRef = useRef(null);
  const cerrarRef = useRef(null);

  useEffect(() => {
    // Al abrir: el foco al botón de cerrar y la página de fondo quieta; al cerrar, todo vuelve.
    const previo = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (cerrarRef.current) cerrarRef.current.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (previo && typeof previo.focus === "function") previo.focus();
    };
  }, []);

  const alTeclear = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onCerrar();
      return;
    }
    if (e.key !== "Tab" || !panelRef.current) return;
    // El foco no sale del visor.
    const lista = [...panelRef.current.querySelectorAll(FOCUSABLES)];
    if (!lista.length) return;
    const primero = lista[0];
    const ultimo = lista[lista.length - 1];
    if (e.shiftKey && document.activeElement === primero) {
      e.preventDefault();
      ultimo.focus();
    } else if (!e.shiftKey && document.activeElement === ultimo) {
      e.preventDefault();
      primero.focus();
    }
  };

  return (
    <div
      style={S.fondo}
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCerrar();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        onKeyDown={alTeclear}
        style={{ ...S.panel, maxWidth: ancho }}
      >
        <div style={S.cabeza}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 id={idTitulo} style={S.titulo}>
              {titulo}
            </h2>
            {subtitulo && <div style={{ ...appType.bodySm, marginTop: 2 }}>{subtitulo}</div>}
          </div>
          {onDescargar && (
            <AppButton variant="outline" onClick={onDescargar} disabled={descargando} style={S.boton} icon={<FaDownload aria-hidden="true" />}>
              {t("historial.descargar")}
            </AppButton>
          )}
          <button ref={cerrarRef} type="button" onClick={onCerrar} aria-label={t("historial.cerrar")} style={S.cerrar}>
            <FaTimes aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Pide una URL al abrir (y cada vez que cambia `clave`): {cargando, error, res, url}.
 * `alListo(res)` corre en el mismo render que la URL (p. ej. cuántas partes hay).
 */
function usePedido(pedir, clave, alListo) {
  const [estado, setEstado] = useState({ cargando: true, error: "", res: null, url: "" });
  useEffect(() => {
    let vigente = true;
    setEstado({ cargando: true, error: "", res: null, url: "" });
    (async () => {
      try {
        const res = await pedir();
        const url = await urlDescargable(res);
        if (!vigente) return;
        if (alListo) alListo(res);
        setEstado({ cargando: false, error: "", res, url });
      } catch (_) {
        if (vigente) setEstado({ cargando: false, error: "error", res: null, url: "" });
      }
    })();
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);
  return estado;
}

/** "Descargar": abre la descarga en otra pestaña; si el navegador la bloquea, deja el link. */
function useDescarga(pedir) {
  const [descargando, setDescargando] = useState(false);
  const [enlace, setEnlace] = useState("");
  const [error, setError] = useState(false);
  const descargar = useCallback(async () => {
    setDescargando(true);
    setError(false);
    setEnlace("");
    try {
      setEnlace(await abrirEnPestana(pedir));
    } catch (_) {
      setError(true);
    } finally {
      setDescargando(false);
    }
  }, [pedir]);
  return { descargar, descargando, enlace, error };
}

/** El acta publicada, en un iframe con el PDF (descarga como adjunto aparte). */
export function VisorActa({ actaId, titulo, subtitulo, onCerrar }) {
  const { t } = useTranslation("asambleas");
  const pedir = useCallback(() => asambleaPropietario("actaPdf", { actaId, descargar: false }), [actaId]);
  const pedirDescarga = useCallback(() => asambleaPropietario("actaPdf", { actaId, descargar: true }), [actaId]);
  const { cargando, error, url } = usePedido(pedir, actaId);
  const descarga = useDescarga(pedirDescarga);
  return (
    <Marco
      titulo={titulo}
      subtitulo={subtitulo}
      onCerrar={onCerrar}
      onDescargar={descarga.descargar}
      descargando={descarga.descargando}
      ancho={980}
    >
      <Avisos t={t} error={error || descarga.error ? t("historial.errorActa") : ""} enlace={descarga.enlace} />
      <div style={S.marcoPdf}>
        {cargando ? (
          <div style={S.centro} aria-live="polite">
            <AppSpinner size={22} thickness={2} centered={false} /> <span style={appType.bodySm}>{t("historial.cargando")}</span>
          </div>
        ) : url ? (
          <iframe title={titulo} src={url} style={S.iframe} />
        ) : null}
      </div>
    </Marco>
  );
}

/** La grabación: video (o audio, si es lo que hay), con sus partes si son varias. */
export function VisorGrabacion({ actaId, titulo, subtitulo, onCerrar }) {
  const { t } = useTranslation("asambleas");
  const [parte, setParte] = useState(0);
  const pedir = useCallback(() => asambleaPropietario("grabacion", { actaId, parte, descargar: false }), [actaId, parte]);
  const pedirDescarga = useCallback(() => asambleaPropietario("grabacion", { actaId, parte, descargar: true }), [actaId, parte]);
  // Cuántas partes hay: queda mientras se pide otra parte (el selector no parpadea).
  const [cantidadPartes, setCantidadPartes] = useState(1);
  const { cargando, error, res, url } = usePedido(pedir, `${actaId}|${parte}`, (r) =>
    setCantidadPartes(Math.max(1, Number(r && r.partes) || 1))
  );
  const descarga = useDescarga(pedirDescarga);
  const esAudio = res && res.tipo === "audio";
  return (
    <Marco
      titulo={titulo}
      subtitulo={esAudio ? t("historial.grabacionAudio") : subtitulo}
      onCerrar={onCerrar}
      onDescargar={descarga.descargar}
      descargando={descarga.descargando}
      ancho={960}
    >
      {cantidadPartes > 1 && (
        <div role="group" aria-label={t("historial.partes")} style={S.partes}>
          {Array.from({ length: cantidadPartes }, (_, i) => (
            <button
              key={i}
              type="button"
              aria-pressed={i === parte}
              onClick={() => setParte(i)}
              style={{ ...S.parte, ...(i === parte ? S.parteActiva : null) }}
            >
              {t("historial.parte", { n: i + 1 })}
            </button>
          ))}
        </div>
      )}
      <Avisos t={t} error={error || descarga.error ? t("historial.errorGrabacion") : ""} enlace={descarga.enlace} />
      {cargando ? (
        <div style={{ ...S.centro, minHeight: 200 }} aria-live="polite">
          <AppSpinner size={22} thickness={2} centered={false} /> <span style={appType.bodySm}>{t("historial.cargando")}</span>
        </div>
      ) : url && esAudio ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio controls src={url} style={{ width: "100%" }} aria-label={titulo} />
      ) : url ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video controls preload="metadata" src={url} style={S.video} aria-label={titulo} />
      ) : null}
    </Marco>
  );
}

function Avisos({ t, error, enlace }) {
  return (
    <>
      {error && (
        <div role="alert" style={{ ...appType.body, color: appColors.red }}>
          {error}
        </div>
      )}
      {enlace && (
        <a href={enlace} target="_blank" rel="noopener noreferrer" style={{ ...appType.body, color: appColors.blue }}>
          {t("historial.abrirPestana")}
        </a>
      )}
    </>
  );
}

const S = {
  fondo: {
    position: "fixed",
    inset: 0,
    background: appColors.scrim,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 10500,
  },
  panel: {
    width: "100%",
    maxHeight: "calc(100vh - 32px)",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    background: appColors.surface,
    borderRadius: appRadius.card,
    boxShadow: appShadows.hero,
    padding: 18,
    fontFamily: appType.fontFamily,
  },
  cabeza: { display: "flex", alignItems: "center", gap: 10 },
  titulo: { ...appType.cardTitle, margin: 0, overflowWrap: "anywhere" },
  boton: { padding: "9px 12px", fontSize: 13.5 },
  cerrar: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: appRadius.iconBox,
    border: `1px solid ${appColors.line}`,
    background: appColors.surface,
    color: appColors.ink2,
    cursor: "pointer",
    flexShrink: 0,
  },
  marcoPdf: {
    height: "min(78vh, 1100px)",
    borderRadius: appRadius.sm,
    overflow: "hidden",
    background: appColors.surface2,
  },
  iframe: { width: "100%", height: "100%", border: 0, display: "block" },
  centro: { height: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 },
  video: { width: "100%", maxHeight: "72vh", borderRadius: appRadius.sm, background: appColors.ink },
  partes: { display: "flex", flexWrap: "wrap", gap: 6 },
  parte: {
    padding: "7px 12px",
    borderRadius: appRadius.pill,
    border: `1px solid ${appColors.line}`,
    background: appColors.surface,
    color: appColors.ink2,
    fontFamily: appType.fontFamily,
    fontSize: 13,
    fontWeight: 400,
    cursor: "pointer",
  },
  parteActiva: { background: appColors.blue, color: appColors.onBlue, border: `1px solid ${appColors.blue}` },
};
